import React, { useEffect, useState } from "react";
import {
  InputAdornment,
  TextField,
  MenuItem,
  Box,
  Typography,
  Avatar,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

const isDarkModeActive = () => {
  if (typeof window !== 'undefined') {
    return document.documentElement.classList.contains('dark');
  }
  return false;
};

const operators = [
  {
    name: "Etisalat",
    prefix: ["78", "73"],
    logo: "/images/mnos/etisalat.jpeg",
  },
  {
    name: "AWCC",
    prefix: ["70", "71"],
    logo: "/images/mnos/awcc.jpeg",
  },
  {
    name: "roshan",
    prefix: ["79", "72"],
    logo: "/images/mnos/roshan.jpeg",
  },
  {
    name: "automa",
    prefix: ["77", "76"],
    logo: "/images/mnos/automa.png",
  },
  {
    name: "salaam",
    prefix: ["74"],
    logo: "/images/mnos/salaam.png",
  },
];

const prefixMap = {
  automa: ["77", "76"], 
  etisalat: ["78", "73"],
  roshan: ["72", "79"],
  salaam: ["74"],
  awcc: ["70", "71"],
};

const countries = [
  { code: "AFN", flag: "https://flagcdn.com/w40/af.png" },
  { code: "PAK", flag: "https://flagcdn.com/w40/pk.png" },
];

const RedTextField = styled(TextField)(({ theme }) => {
  const isDark = isDarkModeActive();
  return {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      height: "46px",
      "& fieldset": {
        borderColor: isDark ? "#4b5563" : "#D0D5DD",
      },
      "&:hover fieldset": {
        borderColor: "red",
      },
      "&.Mui-focused fieldset": {
        borderColor: "red",
      },
    },
    "& .MuiInputBase-input": {
      color: isDark ? "#f3f4f6" : "#1f2937",
    },
    "& .MuiFormHelperText-root": {
      color: isDark ? "#f87171" : "#ef4444",
    },
  };
});

const PhoneInput = ({ getInputValue }) => {
  const { t } = useTranslation();
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState(countries[0]);
  const [operator, setOperator] = useState(null);
  const [error, setError] = useState("");
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(isDarkModeActive());
    };
    
    checkDarkMode();
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });
    
    return () => observer.disconnect();
  }, []);

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    setPhone(value);

    const matched = operators.find((op) =>
      op.prefix.some((prefix) =>
        value.replace(/[^0-9]/g, "").startsWith(prefix)
      )
    );
    setOperator(matched || null);
  };
  
  function validatePhone(input) {
    const digitsOnly = input.replace(/\D/g, "");

    if (digitsOnly.length > 9) {
      setError(t('phoneInput.errors.maxDigits') || "Phone number cannot exceed 9 digits.");
      return false;
    }
    if (digitsOnly.length > 0 && digitsOnly[0] !== '7') {
      setError(t('phoneInput.errors.startWithSeven') || "Phone number must start with 7");
      return false;
    }
    if (digitsOnly.length > 0 && digitsOnly[0] === "0") {
      setError(t('phoneInput.errors.firstDigitZero') || "First digit cannot be 0.");
      return false;
    }

    if (digitsOnly.length >= 2) {
      const prefix = digitsOnly.slice(0, 2);
      const isValidPrefix = Object.values(prefixMap).some((prefixes) =>
        prefixes.includes(prefix)
      );
      if (!isValidPrefix) {
        setError(t('phoneInput.errors.invalidPrefix') || "Invalid mobile network prefix.");
        return false;
      }
    }

    setError(""); 
    return true;
  }

  useEffect(() => {
    getInputValue(phone);
  }, [phone, getInputValue]);

  return (
    <Box sx={{ maxWidth: 500, width: "100%" }}>
      <Typography
        variant="subtitle2"
        sx={{ 
          fontWeight: 400, 
          color: isDark ? "#9ca3af" : "#374151", 
          mb: 1 
        }}
      >
        {t('phoneInput.label') || "Enter Phone Number"}
      </Typography>

      <RedTextField
        fullWidth
        placeholder={t('phoneInput.placeholder') || "(+93) 780-000-000"}
        value={phone}
        onChange={(e) => {
          const validate = validatePhone(e.target.value);
          if (validate) {
            handlePhoneChange(e);
          }
        }}
        error={Boolean(error)}
        helperText={error}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {operator && (
                <Avatar 
                  src={operator.logo} 
                  sx={{ 
                    width: 24, 
                    height: 24,
                    filter: isDark ? 'brightness(0.9)' : 'none'
                  }} 
                />
              )}
              <h6 className="mx-1" style={{ color: isDark ? '#9ca3af' : '#374151', margin: 0 }}>
                +93
              </h6>
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
};

export default PhoneInput;